import { AppEnvironment, AppSettings, AppTenant, KeycloakRealm } from '@customTypes/app';
import { OrderStatus } from '@customTypes/order';

export const KEYCLOAK_URL = 'https://auth.myome.com';
export const KEYCLOAK_CLIENT_ID = 'clinician-portal';

const SENTRY_DSN = 'https://a29e8189dba8d0057153cff2bdb4c862@o4506186993238016.ingest.sentry.io/4506753987575808';

export const MYOME_HOMEPAGE = 'https://myome.com';
export const NATERA_HOMEPAGE = 'https://natera.com';

export const DATE_FORMAT = 'MM/DD/YYYY';
export const REQUISITION_ID_REGEX = /^RQ-?([a-z]{3,5}-?)?\d+$/;

const OPENREPLAY_PROD_PROJECT_KEY = '6NfWRYWOPhmLaDg751Po';
const OPENREPLAY_STG_DEV_PROJECT_KEY = 'DtHwO9jCJZpgrFyZ07aL';
const OPENREPLAY_INGEST_POINT = 'https://openreplay.prd.myome.com/ingest';

const HUBSPOT_ACCOUNT_ID_DEV = '51183565';
const HUBSPOT_ACCOUNT_ID_PRD = '534481739';

const LOCAL_KEYCLOAK_REALM = (process.env.REACT_APP_KEYCLOAK_REALM as KeycloakRealm) || 'dev';

const TURNSTILE_SITEKEY_BY_REALM: Record<string, string> = {
  dev: '0x4AAAAAAFH5-1jluQ7TU8WH',
  stg: '0x4AAAAAAFH6S3m_DD2l6zs1',
  tst: '0x4AAAAAAFH6jwYuPipolbeZ',
  sbx: '0x4AAAAAAFH6kv6rzvc133ml',
  prd: '0x4AAAAAAFH6zMuctYXZR8aR',
};

// natera-stg and stg share one widget.
const turnstileSiteKeyForRealm = (realm: string): string =>
  TURNSTILE_SITEKEY_BY_REALM[realm.replace(/^natera-/, '')] ?? '';

export const ENVIRONMENT_SETTINGS: ReadonlyRecord<string, AppSettings> = {
  tst: {
    baseUrlRegex: /^https:\/\/(clinicians|providers|natera).tst.myome.info(\/.*)?$/,
    apiurl: 'https://api.tst.myome.info/0',
    environment: AppEnvironment.TEST,
    keycloakRealm: 'tst',
    sentryDSN: null,
    googleMapsApiKey: 'AIzaSyD0B_jmB39u-gRWewG4L3Fu_abu2kOY_1c',
    turnstileSiteKey: turnstileSiteKeyForRealm('tst'),
    hubspotAccountId: null,
    tenant: AppTenant.MYOME,
    openreplayProjectKey: OPENREPLAY_STG_DEV_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  sbx: {
    baseUrlRegex: /^https:\/\/(clinicians|providers).sbx.myome.com(\/.*)?$/,
    apiurl: 'https://api.sbx.myome.com/0',
    environment: AppEnvironment.SANDBOX,
    keycloakRealm: 'sbx',
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyD0B_jmB39u-gRWewG4L3Fu_abu2kOY_1c',
    turnstileSiteKey: turnstileSiteKeyForRealm('sbx'),
    hubspotAccountId: HUBSPOT_ACCOUNT_ID_DEV,
    tenant: AppTenant.MYOME,
    openreplayProjectKey: OPENREPLAY_STG_DEV_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  stg: {
    baseUrlRegex: /^https:\/\/(clinicians|providers).stg.myome.info(\/.*)?$/,
    apiurl: 'https://api.stg.myome.info/0',
    environment: AppEnvironment.STAGING,
    keycloakRealm: 'stg',
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyD0B_jmB39u-gRWewG4L3Fu_abu2kOY_1c',
    turnstileSiteKey: turnstileSiteKeyForRealm('stg'),
    hubspotAccountId: HUBSPOT_ACCOUNT_ID_DEV,
    tenant: AppTenant.MYOME,
    openreplayProjectKey: OPENREPLAY_STG_DEV_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  prd: {
    baseUrlRegex: /^https:\/\/(clinicians|providers).myome.com(\/.*)?$/,
    apiurl: 'https://api.myome.com/0',
    environment: AppEnvironment.PROD,
    keycloakRealm: 'prd',
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyD0B_jmB39u-gRWewG4L3Fu_abu2kOY_1c',
    turnstileSiteKey: turnstileSiteKeyForRealm('prd'),
    hubspotAccountId: HUBSPOT_ACCOUNT_ID_PRD,
    tenant: AppTenant.MYOME,
    openreplayProjectKey: OPENREPLAY_PROD_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  dev: {
    baseUrlRegex: /^https:\/\/(clinicians|providers|natera).dev.myome.info(\/.*)?$/,
    apiurl: 'https://api.dev.myome.info/0',
    environment: AppEnvironment.DEV,
    keycloakRealm: 'dev',
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyCDR9y7bIuzn2VXdYy_vaQf1VAZmChEmEs',
    turnstileSiteKey: turnstileSiteKeyForRealm('dev'),
    hubspotAccountId: HUBSPOT_ACCOUNT_ID_DEV,
    tenant: AppTenant.MYOME,
    openreplayProjectKey: OPENREPLAY_STG_DEV_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  local: {
    baseUrlRegex: /^http:\/\/localhost.*$/,
    apiurl: process.env.REACT_APP_API_URL || 'https://api.dev.myome.info/0',
    environment: AppEnvironment.LOCAL,
    keycloakRealm: LOCAL_KEYCLOAK_REALM,
    sentryDSN: null,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY || 'AIzaSyCDR9y7bIuzn2VXdYy_vaQf1VAZmChEmEs',
    turnstileSiteKey: turnstileSiteKeyForRealm(LOCAL_KEYCLOAK_REALM),
    hubspotAccountId: null,
    tenant: AppTenant.MYOME,
    openreplayProjectKey: '',
    openreplayIngestPoint: '',
  },
  vercel: {
    baseUrlRegex: /^(https:\/\/(clinicians|providers)-portal-.*-myome.vercel.app|.*.vcl.myome.com)(\/.*)?$/,
    apiurl: 'https://api.stg.myome.info/0',
    environment: AppEnvironment.STAGING,
    keycloakRealm: 'stg',
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyCDR9y7bIuzn2VXdYy_vaQf1VAZmChEmEs',
    turnstileSiteKey: turnstileSiteKeyForRealm('stg'),
    hubspotAccountId: HUBSPOT_ACCOUNT_ID_DEV,
    tenant: AppTenant.MYOME,
    openreplayProjectKey: OPENREPLAY_STG_DEV_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  vercelNatera: {
    baseUrlRegex: /^(https:\/\/(natera)-portal-.*-myome.vercel.app|.*.vcl.myome.com)(\/.*)?$/,
    apiurl: 'https://api.stg.myome.info/0',
    environment: AppEnvironment.STAGING,
    keycloakRealm: 'stg',
    hubspotAccountId: null,
    tenant: AppTenant.NATERA,
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyCDR9y7bIuzn2VXdYy_vaQf1VAZmChEmEs',
    turnstileSiteKey: turnstileSiteKeyForRealm('stg'),
    openreplayProjectKey: OPENREPLAY_STG_DEV_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  naterastg: {
    baseUrlRegex: /^https:\/\/natera.stg.myome.info(\/.*)?$/,
    apiurl: 'https://api.stg.myome.info/0',
    environment: AppEnvironment.STAGING,
    keycloakRealm: 'stg',
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyD0B_jmB39u-gRWewG4L3Fu_abu2kOY_1c',
    turnstileSiteKey: turnstileSiteKeyForRealm('stg'),
    hubspotAccountId: null,
    tenant: AppTenant.NATERA,
    openreplayProjectKey: OPENREPLAY_STG_DEV_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  naterasbx: {
    baseUrlRegex: /^https:\/\/natera.sbx.myome.com(\/.*)?$/,
    apiurl: 'https://api.sbx.myome.com/0',
    environment: AppEnvironment.SANDBOX,
    keycloakRealm: 'natera-sbx',
    turnstileSiteKey: turnstileSiteKeyForRealm('natera-sbx'),
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyD0B_jmB39u-gRWewG4L3Fu_abu2kOY_1c',
    hubspotAccountId: null,
    tenant: AppTenant.NATERA,
    openreplayProjectKey: OPENREPLAY_STG_DEV_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
  nateraprd: {
    baseUrlRegex: /^https:\/\/raredisease.natera.com(\/.*)?$/,
    apiurl: 'https://api.myome.com/0',
    environment: AppEnvironment.PROD,
    keycloakRealm: 'natera-prd',
    turnstileSiteKey: turnstileSiteKeyForRealm('natera-prd'),
    sentryDSN: SENTRY_DSN,
    googleMapsApiKey: 'AIzaSyD0B_jmB39u-gRWewG4L3Fu_abu2kOY_1c',
    hubspotAccountId: null,
    tenant: AppTenant.NATERA,
    openreplayProjectKey: OPENREPLAY_PROD_PROJECT_KEY,
    openreplayIngestPoint: OPENREPLAY_INGEST_POINT,
  },
};

let appSettings = Object.values(ENVIRONMENT_SETTINGS).find(config => config.baseUrlRegex.exec(window.location.href));

// Default to "dev" if no match
if (!appSettings) {
  // eslint-disable-next-line no-console
  console.error('No environment config found for the current URL. Using default.');
  appSettings = ENVIRONMENT_SETTINGS.dev;
}

const CLI_APP_TENANT = process.env.REACT_APP_THEME_ORG === AppTenant.NATERA ? AppTenant.NATERA : AppTenant.MYOME;

if (appSettings.tenant !== CLI_APP_TENANT) {
  // eslint-disable-next-line no-console
  console.error(`App tenant mismatch. Expected: ${CLI_APP_TENANT} but got ${appSettings.tenant}`);
  // @ts-expect-error
  appSettings.tenant = CLI_APP_TENANT;
}

export const APP_SETTINGS = appSettings;

// Export envInfo for OpenReplay compatibility (provides snake_case property access)
export const envInfo = {
  openreplay_project_key: appSettings.openreplayProjectKey,
  openreplay_ingest_point: appSettings.openreplayIngestPoint,
  keycloak_realm: appSettings.keycloakRealm,
};

export const IS_MYOME = appSettings.tenant === AppTenant.MYOME;
export const IS_NATERA = appSettings.tenant === AppTenant.NATERA;

export const SUPPORT_EMAIL = IS_NATERA ? 'raredisease@natera.com' : 'support@myome.com';

export const BRAND_HOMEPAGE = IS_NATERA ? NATERA_HOMEPAGE : MYOME_HOMEPAGE;
export const BRAND_NAME = IS_NATERA ? 'Natera' : 'MyOme';
export const PORTAL_TITLE = `${BRAND_NAME} Provider's Portal`;
export const POWERED_BY_BRAND = `Powered by ${BRAND_NAME}®`;

interface OrderStatusConfig {
  label: string;
  weight: number;
}

export const ORDER_STATUS_MAPPING: ReadonlyRecord<OrderStatus, OrderStatusConfig> = {
  DENIED: {
    weight: 1,
    label: 'Denied',
  },
  FAILED: {
    weight: 2,
    label: 'Failed',
  },
  SUBMITTED: {
    weight: 3,
    label: 'Order Received',
  },
  APPROVED: {
    weight: 4,
    label: 'Order Received',
  },
  AWAITING_SAMPLE: {
    weight: 5,
    label: 'Order Received',
  },
  ANALYZING: {
    weight: 6,
    label: 'Lab Processing',
  },
  REFERRAL_LAB_LD_REVIEW: {
    weight: 7,
    label: 'Lab Processing',
  },
  CLINICIAN_REVIEW: {
    weight: 8,
    label: 'Clinical Summary Ready',
  },
  COMPLETED: {
    weight: 9,
    label: 'Complete',
  },
  REPORTED: {
    weight: 10,
    label: 'Reported',
  },
  CANCELED: {
    weight: 11,
    label: 'Canceled',
  },
};

export const COMPLETED_ORDER_STATUSES = [OrderStatus.COMPLETED, OrderStatus.REPORTED, OrderStatus.CLINICIAN_REVIEW];

export const ROLE_PLACEHOLDER = 'Select a role';

export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const UI_BUTTON_VAR = IS_NATERA ? 'light' : 'outline-dark';
export const SECTION_CTA_VAR = IS_NATERA
  ? 'text-white bg-primary bg-img-end'
  : 'border-accent-aqua-1 bg-accent-teal-light';
export const PGX_CTA_BTN_VAR = IS_NATERA ? 'pgx-cta' : 'primary';
export const BRAND_PRE = IS_NATERA ? 'nat__' : 'myo__'; // use sparingly

export const PRODUCT_ID_DATA_ONLY = 'PR11014';
export const PRODUCT_ID_SECONDARY_FINDINGS = 'PR21207';
export const PRODUCT_ID_SECONDARY_FINDINGS_NATERA = 'PR21210';
